import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import {
  DEFAULT_LOCALE,
  resolveInitialLocale,
  setHtmlLang,
  setStoredLocale,
  SUPPORTED_LOCALES,
} from '@/shared/lib/locale'
import { importWithRetry } from '@/shared/lib/chunk-recovery'
import {
  DEFAULT_NAMESPACE,
  EAGER_NAMESPACES,
  type LazyNamespace,
} from './namespaces'

import commonPtBR from './locales/common/pt-BR.json'
import commonEn from './locales/common/en.json'
import navigationPtBR from './locales/navigation/pt-BR.json'
import navigationEn from './locales/navigation/en.json'

type Locale = 'en' | 'pt-BR'
type NamespaceLoader = () => Promise<{ default: unknown }>

const NAMESPACE_LOADERS: Record<
  LazyNamespace,
  Record<Locale, NamespaceLoader>
> = {
  login: {
    en: () => import('@/features/auth/i18n/login/en.json'),
    'pt-BR': () => import('@/features/auth/i18n/login/pt-BR.json'),
  },
  'reset-password': {
    en: () => import('@/features/auth/i18n/reset-password/en.json'),
    'pt-BR': () => import('@/features/auth/i18n/reset-password/pt-BR.json'),
  },
  'confirm-account': {
    en: () => import('@/features/auth/i18n/confirm-account/en.json'),
    'pt-BR': () => import('@/features/auth/i18n/confirm-account/pt-BR.json'),
  },
  dashboard: {
    en: () => import('@/features/dashboard/i18n/en.json'),
    'pt-BR': () => import('@/features/dashboard/i18n/pt-BR.json'),
  },
  'finalizers-dashboard': {
    en: () => import('@/features/finalizers-dashboard/i18n/en.json'),
    'pt-BR': () => import('@/features/finalizers-dashboard/i18n/pt-BR.json'),
  },
  processing: {
    en: () => import('@/features/processing/i18n/en.json'),
    'pt-BR': () => import('@/features/processing/i18n/pt-BR.json'),
  },
  reports: {
    en: () => import('@/features/reports/i18n/en.json'),
    'pt-BR': () => import('@/features/reports/i18n/pt-BR.json'),
  },
  'block-order': {
    en: () => import('@/features/block-order/i18n/en.json'),
    'pt-BR': () => import('@/features/block-order/i18n/pt-BR.json'),
  },
  shipments: {
    en: () => import('@/features/shipments/i18n/en.json'),
    'pt-BR': () => import('@/features/shipments/i18n/pt-BR.json'),
  },
  returns: {
    en: () => import('@/features/returns/i18n/en.json'),
    'pt-BR': () => import('@/features/returns/i18n/pt-BR.json'),
  },
  notifications: {
    en: () => import('@/features/notifications/i18n/en.json'),
    'pt-BR': () => import('@/features/notifications/i18n/pt-BR.json'),
  },
  profile: {
    en: () => import('@/features/profile/i18n/en.json'),
    'pt-BR': () => import('@/features/profile/i18n/pt-BR.json'),
  },
  settings: {
    en: () => import('@/features/settings/i18n/en.json'),
    'pt-BR': () => import('@/features/settings/i18n/pt-BR.json'),
  },
  'auth-common': {
    en: () => import('@/features/auth/i18n/auth-common/en.json'),
    'pt-BR': () => import('@/features/auth/i18n/auth-common/pt-BR.json'),
  },
}

const initialLocale = resolveInitialLocale()

i18n
  .use(initReactI18next)
  .init({
    resources: {
      'pt-BR': { common: commonPtBR, navigation: navigationPtBR },
      en: { common: commonEn, navigation: navigationEn },
    },
    ns: [...EAGER_NAMESPACES],
    defaultNS: DEFAULT_NAMESPACE,
    fallbackNS: DEFAULT_NAMESPACE,
    partialBundledLanguages: true,
    lng: initialLocale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    interpolation: {
      escapeValue: false,
    },
    react: {
      bindI18nStore: 'added',
    },
  })
  .catch(error => {
    console.error('[i18n] failed to initialize', error)
  })

setHtmlLang(initialLocale)
setStoredLocale(initialLocale)
i18n.on('languageChanged', lng => {
  setHtmlLang(lng)
  setStoredLocale(lng)
  preloadActiveNamespaces(lng).catch(error => {
    console.error('[i18n] failed to preload namespaces', error)
  })
})

export async function loadNamespace(
  ns: LazyNamespace,
  lang: string = i18n.language,
): Promise<void> {
  if (i18n.hasResourceBundle(lang, ns)) return
  const loader = NAMESPACE_LOADERS[ns]?.[lang as Locale]
  if (!loader) {
    throw new Error(`i18n: no loader registered for "${ns}" in "${lang}"`)
  }
  const mod = await importWithRetry(loader)
  i18n.addResourceBundle(lang, ns, mod.default, true, true)
}

export async function preloadActiveNamespaces(
  lang: string = i18n.language,
): Promise<void> {
  const active = i18n.reportNamespaces?.getUsedNamespaces?.() ?? []
  const lazyNamespaces = Object.keys(NAMESPACE_LOADERS) as LazyNamespace[]
  const toLoad = lazyNamespaces.filter(
    ns =>
      active.includes(ns) ||
      SUPPORTED_LOCALES.some(locale => i18n.hasResourceBundle(locale, ns)),
  )
  await Promise.all(toLoad.map(ns => loadNamespace(ns, lang)))
}

export default i18n
